import { useMemo } from 'react'
import { Checkbox, Space, Tag, Tooltip, Typography } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { PERMISSIONS, type Permission } from '@/shared/auth/permissions'
import { usePermissions } from '@/shared/auth/usePermissions'
import { permissionLabel } from './permissionLabel'

/**
 * Huquqlarni domen bo'yicha guruhlaydi: `subscriber.session.disconnect`
 * → `subscriber`. 112 ta huquqni bitta uzun ro'yxatda ko'rsatish
 * operatorni ko'mib yuborardi.
 */
function domainOf(code: string): string {
  // Nuqtasiz kod bo'lmasligi kerak, lekin bo'lsa ham guruhsiz qolmasin
  return code.split('.')[0] ?? code
}

interface Props {
  value: readonly string[]
  onChange: (next: string[]) => void
  /** `GET /api/roles/permissions` dan — qaysi huquq XAVFLI deb belgilangan */
  dangerous: ReadonlySet<string>
  disabled?: boolean
}

/**
 * Rol uchun huquqlarni tanlash.
 *
 * <h2>⚠️ O'ZIDA BO'LMAGAN HUQUQ BELGILANMAYDI</h2>
 * Server o'zida bo'lmagan huquqni berishga urinishni rad etadi
 * (`RoleServiceImpl#requireNoEscalation`). Agar panel ularni belgilashga
 * qo'ysa, operator butun formani to'ldirib, saqlash tugmasini bosgach
 * "ruxsat yo'q" degan xabar olardi — va QAYSI huquq sababchi ekanini
 * bilmasdi. Shuning uchun bunday huquqlar o'chirilgan holda turadi va
 * sababi tooltip'da yozilgan.
 *
 * <h2>⚠️ XAVFLI HUQUQLAR ALOHIDA BELGILANADI</h2>
 * Ular ro'yxatdan ajralib turishi kerak: pul tuzatish, depozit qo'shish,
 * rol berish, parol o'rnatish, to'liq telefon raqamini ochish. Bularni
 * boshqalar bilan bir xil ko'rsatish rolni tuzayotgan odamga "hammasi
 * bir xil daraja" degan noto'g'ri tasavvur berardi.
 */
export function PermissionPicker({ value, onChange, dangerous, disabled }: Props) {
  const { t } = useTranslation()
  const { permissions: mine } = usePermissions()

  const groups = useMemo(() => {
    const map = new Map<string, Permission[]>()
    for (const code of PERMISSIONS) {
      const domain = domainOf(code)
      const list = map.get(domain) ?? []
      list.push(code)
      map.set(domain, list)
    }
    return [...map.entries()]
  }, [])

  const selected = useMemo(() => new Set(value), [value])

  const toggle = (code: string, checked: boolean) => {
    const next = new Set(selected)
    if (checked) next.add(code)
    else next.delete(code)
    onChange([...next])
  }

  /** Guruhdagi men bera oladigan huquqlar — "hammasini belgilash" shu bo'yicha */
  const grantableOf = (codes: readonly Permission[]) => codes.filter((c) => mine.has(c))

  return (
    <div style={{ maxHeight: 420, overflowY: 'auto', paddingInlineEnd: 8 }}>
      {groups.map(([domain, codes]) => {
        const grantable = grantableOf(codes)
        const chosen = grantable.filter((c) => selected.has(c))
        return (
          <div key={domain} style={{ marginBottom: 14 }}>
            <Space size={8} style={{ marginBottom: 4 }}>
              <Checkbox
                // ⚠️ Faqat BERISH MUMKIN bo'lganlar ustidan ishlaydi:
                // aks holda "hammasini belgilash" server rad etadigan
                // to'plamni yasab qo'yardi.
                indeterminate={chosen.length > 0 && chosen.length < grantable.length}
                checked={grantable.length > 0 && chosen.length === grantable.length}
                disabled={disabled || grantable.length === 0}
                onChange={(e) => {
                  const next = new Set(selected)
                  for (const c of grantable) {
                    if (e.target.checked) next.add(c)
                    else next.delete(c)
                  }
                  onChange([...next])
                }}
              >
                <Typography.Text strong>{t(`permissionGroup.${domain}`, domain)}</Typography.Text>
              </Checkbox>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {chosen.length}/{codes.length}
              </Typography.Text>
            </Space>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 16px', paddingInlineStart: 24 }}>
              {codes.map((code) => {
                const canGrant = mine.has(code)
                const box = (
                  <Checkbox
                    key={code}
                    checked={selected.has(code)}
                    disabled={disabled || !canGrant}
                    onChange={(e) => toggle(code, e.target.checked)}
                    style={{ minWidth: 300 }}
                  >
                    {/*
                      ⚠️ BUG-042 — YORLIQ ODAM TILIDA, kod IKKILAMCHI.
                      Ilgari bu yerda `{code}` yolg'iz turardi va 140 ta
                      katakchadan 98 tasi `subscriber.read` ko'rinishida
                      chizilardi (o'lchandi 2026-08-16). Kod ATAYLAB
                      qoldirildi — sabab `permissionLabel` javadoc'ida.
                    */}
                    <Space size={4} align="center">
                      <Space direction="vertical" size={0}>
                        <span style={{ fontSize: 13 }}>{permissionLabel(code, t)}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {code}
                        </Typography.Text>
                      </Space>
                      {dangerous.has(code) ? (
                        <Tooltip title={t('roles.dangerousHint')}>
                          <Tag color="red" style={{ marginInlineEnd: 0 }}>
                            <WarningOutlined /> {t('roles.dangerous')}
                          </Tag>
                        </Tooltip>
                      ) : null}
                    </Space>
                  </Checkbox>
                )
                return canGrant ? (
                  box
                ) : (
                  <Tooltip key={code} title={t('roles.notMine')}>
                    {/* Tooltip o'chirilgan elementda ishlamaydi — o'rovchi kerak */}
                    <span>{box}</span>
                  </Tooltip>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
